import { Box, Flex } from "@chakra-ui/layout";
import BottomTableFixed from "components/BottomTableFixed";
import ModalNovoAcordo from "components/Features/Acordo/NovoAcordo";
import { useLoading } from "components/GlobalLoading";
import useSaveAcordo from "features/acordo/hooks/useSaveAcordo";
import useCobrancaContasComposicoes from "features/cobranca/hooks/useCobrancaContasComposicoes";
import {
  useDeleteConta,
  useCancelarCobranca,
  useExportarRemessa,
  useReenviarEmailConta,
  useReprocessarConta,
  useCriarGrupoImpressaoBoleto,
} from "features/conta/hooks";
import config from "infra/config";
import { useContaStore } from "features/conta/store";
import { ContaItemResponse } from "features/conta/typings";
import {
  showConfirm,
  showError,
  showErrorAxios,
  showSuccess,
} from "infra/helpers/alerta";
import {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { OperacaoEnum } from "resources/enums/conta";
import ListingPageHeaderBar from "ui/components/Page/ListingPageHeaderBar";
import { actions } from "./actions";
import ItemConta from "./components/ItemConta/index";
import TableTags from "./TableTags";
import InfiniteScrollCardList from "ui/components/InfiniteScrollCardList";
import { TipoTenantEnum } from "resources/enums/tenant";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import { Buffer } from "buffer";

interface ContasCardsProps {
  isLoading: boolean;
  data?: ContaItemResponse[];
  refresh: any;
  onRowClick?: (id: string, ev: React.MouseEvent<HTMLDivElement>) => void;
  onEdit: (id: string) => void;
  onLiquidar: (id: string) => void;
  onEstornar?: (id: string) => void;
  onViewHistory?: (id: string) => void;
  onViewHistoryProtesto?: (id: string) => void;
  onChangeSelectedRows?: (selectedRows: ContaItemResponse[]) => any;
  setIsOpenModalFiltros?: (v: any) => void;
  hasNextPage: boolean;
  fetchNextPage: () => any;
  setLiquidarContasData: React.Dispatch<
    React.SetStateAction<ContaItemResponse[]>
  >;
}

const ContasCards = forwardRef(
  (
    {
      isLoading,
      data,
      onRowClick,
      onEdit,
      onLiquidar,
      onEstornar,
      onViewHistory,
      onViewHistoryProtesto,
      onChangeSelectedRows,
      refresh,
      setIsOpenModalFiltros,
      fetchNextPage,
      hasNextPage,
      setLiquidarContasData,
    }: ContasCardsProps,
    ref?: React.ForwardedRef<{
      unselectAll: () => any;
    }>,
  ) => {
    const { tenant } = useCurrentTenant();
    const [isOpenModalAcordo, setIsOpenModalAcordo] = useState(false);

    const {
      filters: { operacao, search, orderBy, orderDirection },
      update,
      filters,
    } = useContaStore();

    const { mutate: handleDeleteConta, isLoading: isLoadingDelete } =
      useDeleteConta();

    const { mutate: handleReprocessarConta, isLoading: isLoadingReprocessar } =
      useReprocessarConta();

    const {
      mutate: handleCancelarCobrancaConta,
      isLoading: isLoadingCancelarCobranca,
    } = useCancelarCobranca();

    const { mutate: reenviarConta, isLoading: isLoadingReenviar } =
      useReenviarEmailConta();

    const { mutate: exportarRemessa, isLoading: isLoadingRemessa } =
      useExportarRemessa();

    const { mutate: criarGrupoImpressao, isLoading: isLoadingGrupoImpressao } =
      useCriarGrupoImpressaoBoleto();

    const [selectedRows, setSelectedRows] = useState<ContaItemResponse[]>([]);

    const { mutate: handleSave, isLoading: isLoadingSave } = useSaveAcordo();

    useLoading(
      isLoadingDelete ||
        isLoadingCancelarCobranca ||
        isLoadingRemessa ||
        isLoadingReenviar ||
        isLoadingReprocessar,
    );

    const allSelected = data?.every(
      (conta) =>
        !!selectedRows.find((selectedItem) => selectedItem.id === conta.id),
    );

    const someSelected = data?.some(
      (conta) =>
        !!selectedRows.find((selectedItem) => selectedItem.id === conta.id),
    );

    const indeterminate = someSelected && !allSelected;

    const onSelectAll = () => {
      if (data) {
        setSelectedRows(data);
      }
    };

    const onUnselectAll = useCallback(() => {
      setSelectedRows((state) =>
        state.filter(
          (selectedItem) =>
            !data?.find(
              (categoriaSelectItem) =>
                selectedItem.id === categoriaSelectItem.id,
            ),
        ),
      );
    }, [data]);

    useImperativeHandle(ref, () => ({
      unselectAll: onUnselectAll,
    }));

    const handleDelete = async (ids: string[]) => {
      const { isConfirmed } = await showConfirm();

      if (isConfirmed) {
        handleDeleteConta(ids, {
          onSuccess: async () => {
            setSelectedRows([]);

            await showSuccess();

            refresh();
          },
        });
      }
    };

    const handleCancelarCobranca = async (ids: string[]) => {
      const { isConfirmed } = await showConfirm(
        ids.length === 1
          ? "Deseja realmente cancelar esta cobrança?"
          : "Deseja realmente cancelar estas cobranças?",
      );

      if (isConfirmed) {
        handleCancelarCobrancaConta(ids, {
          onSuccess: async () => {
            setSelectedRows([]);

            await showSuccess(
              ids.length === 1
                ? "Cobrança cancelada com sucesso!"
                : "Cobranças canceladas com sucesso!",
            );

            refresh();
          },
        });
      }
    };

    const handleLiquidar = async (ids: string[]) => {
      if (ids.length > 1) {
        const items = data?.filter((item) => ids.includes(item.id));
        return setLiquidarContasData(items ?? []);
      }
      return onLiquidar(ids[0]);
    };

    useEffect(() => {
      onChangeSelectedRows?.(selectedRows);
    }, [onChangeSelectedRows, selectedRows]);

    const handleVerBoletos = (rows: ContaItemResponse[]) => {
      if (rows.length > 1) {
        const hasSelectedWithoutBoleto = !!rows.find((row) => !row.linkBoleto);

        if (hasSelectedWithoutBoleto) {
          showError("Alguma conta selecionada não possui boleto");
          return;
        }

        const ids = rows.map((row) => row.id);

        criarGrupoImpressao(ids, {
          onSuccess: ({ hash }) => {
            window.open(
              `${
                config.urlApi
              }/v1/boleto/multiplo/grupo/${hash}?html=true&date=${+new Date()}`,
              "_blank",
            );
          },
        });
      } else {
        if (rows[0]?.linkBoleto) {
          window.open(
            `${rows[0].linkBoleto}?html=true&date=${+new Date()}`,
            "_blank",
          );
        } else {
          showError("Esta conta não possui boleto");
        }
      }
    };

    const handleBaixarPdfsIndividuais = (rows: ContaItemResponse[]) => {
      const hasSelectedWithoutBoleto = !!rows.find((row) => !row.linkBoleto);

      if (hasSelectedWithoutBoleto) {
        showError("Alguma conta selecionada não possui boleto");
        return;
      }

      const ids = rows.map((row) => row.id);

      criarGrupoImpressao(ids, {
        onSuccess: ({ hash }) => {
          window.open(`/financeiro/download-boletos/${hash}`, "_blank");
        },
      });
    };

    const handleExportar = (ids: string[]) => {
      exportarRemessa(ids, {
        onSuccess: async (result) => {
          for (const data of result) {
            const url = window.URL.createObjectURL(
              new Blob([Buffer.from(data.bytes)]),
            );
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", data.nome);

            // Append to html link element page
            document.body.appendChild(link);

            // Start download
            link.click();

            // Clean up and remove the link
            link.parentNode?.removeChild(link);
          }

          showSuccess("Remessa exportada com sucesso!");
        },
      });
    };

    const numFiltros =
      (filters.categoriasFinanceirasIds?.length ? 1 : 0) +
      (filters.contasBancariasIds?.length ? 1 : 0) +
      (filters.formasPagamentoIds?.length ? 1 : 0) +
      (filters.statusBoletoId ? 1 : 0) +
      (filters.unidadeId ? 1 : 0) +
      (filters.pessoaId ? 1 : 0) +
      (filters.centrosDeCustoIds?.length ? 1 : 0) +
      (filters.dataInicio && filters.dataFim ? 1 : 0) +
      (filters.contemAnexoIds?.length ? 1 : 0) +
      (filters.naoContemAnexoIds?.length ? 1 : 0) +
      (filters.tipoLiquidacao ? 1 : 0) +
      (filters.gruposUnidadesIds?.length ? 1 : 0) +
      (filters.unidadesIds?.length ? 1 : 0);

    const orderByOptions = [
      {
        label: "Data de Vencimento",
        value: "c.dataVencimento",
      },
      {
        label: "Data de Liquidação",
        value: "c.dataPagamento",
      },
      {
        label: "Data de Movimentação Bancária",
        value: "c.dataMovimentacao",
      },
      {
        label: "Resumo",
        value: "c.descricao",
      },
    ];

    if (operacao === OperacaoEnum.Despesa) {
      orderByOptions.push({
        label: "Fornecedor",
        value: "pessoa.nome",
      });
    } else {
      orderByOptions.push({
        label: "Devedor",
        value: "pessoa.nome",
      });
    }

    orderByOptions.push({
      label: "Valor",
      value: "c.valor",
    });

    if (tenant.tipoTenantId === TipoTenantEnum.Condominio) {
      orderByOptions.push({
        label: "Unidade",
        value: "bq.nome",
      });
    }

    return (
      <>
        {isOpenModalAcordo ? (
          <ModalNovoAcordo
            unidadeId={selectedRows?.[0]?.unidadeId}
            isOpen={isOpenModalAcordo}
            onClose={() => {
              setIsOpenModalAcordo(false);
            }}
            fetchComposicoesHook={useCobrancaContasComposicoes}
            fetchComposicoesHookParams={{
              unidadeId: selectedRows?.[0]?.unidadeId,
              contas: selectedRows?.map((c) => c.id),
            }}
            isLoadingSave={isLoadingSave}
            onSubmit={(data) => {
              handleSave(
                {
                  ...data,
                  contas: selectedRows.map((c) => c.id),
                },
                {
                  onSuccess: async () => {
                    await showSuccess();

                    setSelectedRows([]);
                    setIsOpenModalAcordo(false);
                    refresh();
                  },
                  onError: (err: any) => {
                    showErrorAxios(err);
                  },
                },
              );
            }}
          />
        ) : null}

        <Box mb={6}>
          <ListingPageHeaderBar
            valueSearch={search as any}
            onSearch={(q?: string) => update("search", q)}
            visibleFiltro
            badge
            badgeValue={numFiltros}
            onFiltro={() => setIsOpenModalFiltros!(true)}
            children={
              <Flex>
                <TableTags />
              </Flex>
            }
            showOrderByButton
            orderByOptions={orderByOptions}
            orderBy={orderBy}
            onOrderByChange={(v) => {
              if (["c.dataPagamento", "c.dataMovimentacao"].includes(v)) {
                update({
                  pago: true,
                  atraso: null,
                  orderBy: v,
                });
              } else {
                update("orderBy", v);
              }
            }}
            orderDirection={orderDirection}
            onOrderDirectionChange={(v) => update("orderDirection", v)}
            checkbox={!!data?.length}
            checkAll={allSelected}
            indeterminate={indeterminate}
            onCheckAllChange={allSelected ? onUnselectAll : onSelectAll}
          />
        </Box>

        <Box mt={4} paddingBottom={8}>
          <InfiniteScrollCardList
            isLoading={isLoading}
            isFetchingNextPage={isLoading}
            data={data}
            hasMore={!!hasNextPage}
            onNext={fetchNextPage}
            emptyDescription="Sem contas para esse filtro"
            RenderCard={({ index, item }) => (
              <ItemConta
                operacao={operacao}
                onRowClick={onRowClick}
                handleDelete={handleDelete}
                handleCancelarCobranca={handleCancelarCobranca}
                handleReprocessarConta={handleReprocessarConta}
                onEdit={onEdit}
                onLiquidar={handleLiquidar}
                reenviarConta={reenviarConta}
                refresh={refresh}
                onEstornar={onEstornar}
                onViewHistory={onViewHistory}
                onViewHistoryProtesto={onViewHistoryProtesto}
                item={item}
                indexItem={index}
                key={item.id}
                selected={
                  !!selectedRows.find(
                    (selectedItem) => selectedItem.id === item.id,
                  )
                }
                onSelect={() => {
                  setSelectedRows([...selectedRows, item]);
                }}
                onUnselect={() => {
                  setSelectedRows(
                    selectedRows.filter(
                      (selectedItem) => !(selectedItem.id === item.id),
                    ),
                  );
                }}
              />
            )}
          />
        </Box>

        {selectedRows?.length ? (
          <BottomTableFixed
            selectedRows={selectedRows}
            onClose={() => {
              setSelectedRows([]);
            }}
            actions={actions(selectedRows, operacao!, {
              onReprocessarConta: handleReprocessarConta,
              onDelete: handleDelete,
              onCancelarCobranca: handleCancelarCobranca,
              onExportar: handleExportar,
              onLiquidar: handleLiquidar,
              onAcordo: () => setIsOpenModalAcordo(true),
              onEmail: reenviarConta,
              onVerBoletos: handleVerBoletos,
              onBaixarPdfsIndividuais: handleBaixarPdfsIndividuais,
              isCreatingGrupo: isLoadingGrupoImpressao,
              refresh,
            })}
          />
        ) : null}
      </>
    );
  },
);

export default ContasCards;
